import { useState } from 'react'

import { addYears, subYears } from 'date-fns'
import {
  type DateRange,
  type DayPickerProps,
  DayPicker,
} from 'react-day-picker'
import 'react-day-picker/style.css'

import { DatePickerPresets } from './components/DatePickerPresets'
import { DatePickerStyled } from './styles/DatePickerStyled'

type DatePickerSingleProps = {
  mode: 'single'
  selected?: Nullable<Date>
  onSelect?: (date: Date | undefined | null) => void
}

type DatePickerRangeProps = {
  mode: 'range'
  selected?: Nullable<DateRange>
  onSelect?: (range: DateRange | undefined | null) => void
}

type DatePickerModeProps = DatePickerSingleProps | DatePickerRangeProps

export type IDateCalendarProps = DatePickerModeProps & {
  'aria-label'?: string
  /** Controlled month display */
  month?: Date
  /** Called when the displayed month changes */
  onMonthChange?: (month: Date) => void
  /** Function to disable specific dates */
  disabled?: DayPickerProps['disabled']
  /** Number of months to show */
  numberOfMonths?: number
  /** Locale for calendar display */
  locale?: DayPickerProps['locale']
  /** Earliest selectable month in month/year dropdown */
  fromMonth?: Date
  /** Latest selectable month in month/year dropdown */
  toMonth?: Date
  /** Preset options displayed beside the calendar */
  presets?: { label: string }[]
  /** Title for the presets section */
  presetTitle?: string
  /** Called when a preset is clicked */
  onPresetClick?: (preset: { label: string }) => void
  /** Initial month shown */
  defaultMonth?: Date
}

export const DateCalendar = ({
  'aria-label': ariaLabel,
  mode,
  selected,
  onSelect,
  month: monthProp,
  onMonthChange: onMonthChangeProp,
  disabled,
  numberOfMonths,
  locale,
  fromMonth,
  toMonth,
  presets,
  presetTitle,
  onPresetClick,
  defaultMonth,
}: IDateCalendarProps) => {
  const [internalMonth, setInternalMonth] = useState<Date>(() => {
    if (monthProp) return monthProp
    if (mode === 'single' && selected instanceof Date) return selected
    if (mode === 'range' && selected?.from) return selected.from
    return new Date()
  })

  const month = monthProp ?? internalMonth
  const handleMonthChange = onMonthChangeProp ?? setInternalMonth

  const resolvedNumberOfMonths = numberOfMonths ?? (mode === 'range' ? 2 : 1)

  const dayPickerProps: DayPickerProps =
    mode === 'single'
      ? {
          mode: 'single',
          selected,
          onSelect,
        }
      : {
          mode: 'range',
          selected,
          onSelect,
        }

  return (
    <DatePickerStyled aria-label={ariaLabel}>
      <DayPicker
        {...dayPickerProps}
        month={month}
        onMonthChange={handleMonthChange}
        disabled={disabled}
        numberOfMonths={resolvedNumberOfMonths}
        locale={locale}
        captionLayout='dropdown'
        startMonth={fromMonth || subYears(new Date(), 10)}
        endMonth={toMonth || addYears(new Date(), 10)}
        autoFocus
        navLayout='around'
        animate
        defaultMonth={defaultMonth}
      />

      {presets && onPresetClick && (
        <DatePickerPresets
          presets={presets}
          title={presetTitle}
          onClick={onPresetClick}
        />
      )}
    </DatePickerStyled>
  )
}

DateCalendar.displayName = 'DateCalendar'
